import axios from 'axios';

export interface Tenant {
  _id: string;
  tenantId: string;
  name: string;
  farmTimezone: string;
  createdAt?: string;
  /** Full tenant document includes more fields (logoUrl, weatherThresholds, scoutingFreshnessThresholds, centralLocation, weatherSyncStatus). The system-admin pages only consume the four above. */
  [key: string]: any;
}

interface TenantInput {
  tenantId: string;
  name: string;
  farmTimezone: string;
}

export const getTenantInfo = async () => {
  const response = await axios.get('/api/v1/tenants/me', {
    withCredentials: true
  });
  return response.data;
};

export const updateTenantInfo = async (data: any) => {
  const response = await axios.put('/api/v1/tenants/me', data, {
    withCredentials: true
  });
  return response.data;
};

export const getSuggestedFarmLocation = async () => {
  const response = await axios.get('/api/v1/tenants/me/suggested-farm-location', {
    withCredentials: true
  });
  return response.data as {
    centralLocation: { type: 'Point'; coordinates: [number, number] } | null;
    source: 'orchards' | 'none';
    orchardCount: number;
  };
};

export const triggerWeatherBackfill = async () => {
  const response = await axios.post('/api/v1/tenants/me/weather-backfill', {}, {
    withCredentials: true
  });
  return response.data as { message: string };
};

export const captureCurrentWeather = async () => {
  const response = await axios.post('/api/v1/tenants/me/weather/capture-now', {}, {
    withCredentials: true
  });
  return response.data as {
    message: string;
    entry: {
      timestamp: string;
      temp?: number;
      windSpeed?: number;
      precipitation?: number;
      condition?: string;
      humidity?: number;
      pressure?: number;
    };
  };
};

export const uploadLogo = async (file: File) => {
  const formData = new FormData();
  formData.append('logo', file);

  const response = await axios.post('/api/v1/tenants/logo', formData, {
    withCredentials: true,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export async function listTenants(): Promise<Tenant[]> {
  const response = await axios.get<Tenant[]>('/api/v1/tenants', { withCredentials: true });
  return response.data;
}

export async function createTenant(input: TenantInput): Promise<Tenant> {
  const response = await axios.post<Tenant>('/api/v1/tenants', input, { withCredentials: true });
  return response.data;
}

/**
 * Update a tenant from the system-admin context. Uses the `/me?tenantId=` Super
 * Admin override on the backend; the requester must have `tenantId === 'system'`.
 * Pass only the editable fields (`name`, `farmTimezone`).
 */
export async function updateTenant(targetTenantId: string, input: Pick<TenantInput, 'name' | 'farmTimezone'>): Promise<Tenant> {
  const response = await axios.put<Tenant>(
    '/api/v1/tenants/me',
    input,
    { params: { tenantId: targetTenantId }, withCredentials: true }
  );
  return response.data;
}
